import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import * as Trader from "trader.ts";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { selectbox } from "trader.ts/ui.common";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import * as types from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { capitalize } from "../../lib/capitalize";
import { isEqual } from "../../lib/isequal";
import { isNil } from "../../lib/istype";
import {
  getFromLocalStorage,
  localStorageSpec,
  setLocalStorage,
} from "../../lib/localstorage";
import { partition } from "../../lib/partition";
import { instantC } from "../../lib/time";
import {
  formulaType,
  parseEnrollmentRequestStatus,
  parseWeekday,
} from "../../parsing_from_ur";
import {
  ddMMMMyyyy,
  ddMMMyyyy,
  renderFirstNameLastName,
  renderFormulaTypeShort,
  renderTimePlusWeekday,
  renderTimeRangePlusWeekday,
} from "../../render";
import * as routes from "../../routes";
import { stringToJsxClientside } from "../editemailtemplates/client";

export type enrollmentStudentStart = {
  student: {
    firstname: string;
    lastname: string;
    id: types.studentid;
  };
  id: types.enrollmentid;
  status: types.enrollment_status;
  lessongroup: {
    id: types.lessongroupid;
    firstlesson: LocalDate | null;
    lastlesson: LocalDate | null;
    time: LocalTime;
    weekday: types.weekday;
    roomid: types.roomid | null;
    location: types.lesson_location;
  };
  instrument: {
    description: string;
    id: types.instrumentid;
  };
  teacher: {
    id: types.teacherid;
    internalteacherid: types.internalteacherid;
    firstname: string;
    lastname: string;
  };
  has_mutuality_certificate: boolean;
  room_description: string | null;
  evaluation_count_for_students: number;
};

type privateEnrollmentrequest = {
  student: {
    id: types.studentid;
    firstname: string;
    lastname: string;
  };
  id: types.enrollmentrequestid;
  status: types.enrollmentrequest_status;
  reserveduntil: LocalDate | null;
  time: LocalTime;
  weekday: types.weekday;
  firstlesson: LocalDate;
  instrument: {
    description: string;
  };
  teacher: {
    firstname: string | null;
    lastname: string | null;
  };
};
type groupEnrollmentrequest = {
  student: {
    id: types.studentid;
    firstname: string;
    lastname: string;
  };
  id: types.enrollmentrequestid;
  status: types.enrollmentrequest_status;
  reserveduntil: LocalDate | null;
  time: LocalTime;
  weekday: types.weekday;
  firstlesson: LocalDate | null;
  type_: types.formula_type;
  instrument: {
    description: string;
  };
  teacher: {
    firstname: string;
    lastname: string;
  };
};
type waitinglistprivateitem = {
  id: types.waitinglistid;
  student: {
    id: types.studentid;
    firstname: string;
    lastname: string;
  };
  instrument: {
    description: string;
  };
  teacher: {
    firstname: string | null;
    lastname: string | null;
  };
  ranges:
    | { weekday: types.weekday; starttime: LocalTime; endtime: LocalTime }[]
    | null;
  formulas: { description: string }[] | null;
};

type waitinglistgroupitem = {
  id: types.waitinglistlessongroupid;
  student: {
    id: types.studentid;
    firstname: string;
    lastname: string;
  };
  instrument: {
    description: string;
  };
  teacher: {
    firstname: string | null;
    lastname: string | null;
  };
  firstlesson: LocalDate | null;
  type: formulaType;
};

export const studentSectionStudentidSelectionLSSpec: localStorageSpec<
  | {
      tag: "all";
      stamp: Instant;
    }
  | { tag: "studentid"; studentid: types.studentid; stamp: Instant }
> = {
  key: "classy-student-section-studentid-selection-with-stamp",
  codec: c.oneOf([
    c.Codec.interface({ tag: c.exactly("all"), stamp: instantC }),
    c.Codec.interface({
      tag: c.exactly("studentid"),
      studentid: routes.newtypeCodec<types.studentid>(),
      stamp: instantC,
    }),
  ]),
};

export function render(opts: {
  schoolname: string;
  message: string | null;
  enrollments: enrollmentStudentStart[];
  privateEnrollmentrequests: privateEnrollmentrequest[];
  groupEnrollmentrequests: groupEnrollmentrequest[];
  waitinglistprivateitems: waitinglistprivateitem[];
  waitinglistgroupitems: waitinglistgroupitem[];
}): HTMLElement | HTMLElement[] {
  const students = opts.enrollments
    .map((e) => ({
      id: e.student.id,
      firstname: e.student.firstname,
      lastname: e.student.lastname,
    }))
    .concat(
      opts.privateEnrollmentrequests.map((er) => ({
        id: er.student.id,
        firstname: er.student.firstname,
        lastname: er.student.lastname,
      })),
    )
    .concat(
      opts.groupEnrollmentrequests.map((er) => ({
        id: er.student.id,
        firstname: er.student.firstname,
        lastname: er.student.lastname,
      })),
    )
    .uniq()
    .orderBy(renderFirstNameLastName);

  const studentS = studentSectionInitStudentidSelection(students);

  //  This isn't very safe, but:
  // - it's only changable by admins
  // - rendered serverside only
  // As long as we keep it like this, it's OK
  const messRendered = isNil(opts.message) ? (
    <span></span>
  ) : (
    <div
      className={style.full_width_center_800}
      style="background-color: white; border-top: 1px solid var(--grey-200); "
    >
      <div
        style=" white-space: pre-line; font-size: 16px !important;"
        className={
          style.card +
          style.card_accent +
          style.card_large +
          style.message_on_student_profiles
        }
      >
        {
          Array.from(
            stringToJsxClientside("<div>" + opts.message + "</div>").childNodes,
          ) as HTMLElement[]
        }
      </div>
    </div>
  );

  return dynVariadic([studentS], ([student]) => {
    function renderMessageAndList() {
      const enrollmentRequestsForStudent = [
        ...opts.privateEnrollmentrequests,
        ...opts.groupEnrollmentrequests,
      ].filter((e) =>
        student.tag === "all" ? true : e.student.id === student.id,
      );
      const enrollmentsForStudent = opts.enrollments.filter((e) =>
        student.tag === "all" ? true : e.student.id === student.id,
      );
      const waitinglistPrivateForStudent = opts.waitinglistprivateitems.filter(
        (e) => (student.tag === "all" ? true : e.student.id === student.id),
      );
      const waitinglistGroupForStudent = opts.waitinglistgroupitems.filter(
        (e) => (student.tag === "all" ? true : e.student.id === student.id),
      );
      return [
        messRendered,
        studentSectionRenderStudentSelection(students, studentS),
        <main
          className={style.full_width_center_800}
          style="max-width: unset; padding-bottom: 128px;"
        >
          {renderList(
            student,
            enrollmentRequestsForStudent,
            enrollmentsForStudent,
            waitinglistPrivateForStudent,
            waitinglistGroupForStudent,
          )}
        </main>,
      ];
    }

    return renderMessageAndList();
  });
}

function renderList(
  student: { tag: "all" } | { tag: "student"; firstname: string },
  enrollmentrequests: (privateEnrollmentrequest | groupEnrollmentrequest)[],
  enrollments: enrollmentStudentStart[],
  waitinglistprivateitems: waitinglistprivateitem[],
  waitinglistgroupitems: waitinglistgroupitem[],
) {
  const today = LocalDate.now();
  const renderedEnrollmentRequests = mapPartial(enrollmentrequests, (er) =>
    renderEnrollmentRequest(student, er),
  );

  function isOlderEnrollment(e: {
    lessongroup: { lastlesson: LocalDate | null };
  }) {
    const ll = e.lessongroup.lastlesson || LocalDate.of(2000, 1, 1);
    return ll.isBefore(today.minusMonths(1));
  }

  const enrollmentsSorted = enrollments.orderBy(
    (e) => (e.lessongroup.lastlesson || LocalDate.of(2000, 1, 1)).toEpochDay(),
    "desc",
  );
  const [enrollmentsRecent, enrollmentsOlder] = partition(
    enrollmentsSorted,
    (e) => !isOlderEnrollment(e),
  );

  const showEnrollmentsOlderS = new Source(enrollmentsRecent.length === 0);

  return (
    <div>
      {renderedEnrollmentRequests.length > 0 ? (
        <label>
          {student.tag === "all"
            ? "aanvragen"
            : "%studentfirstname's aanvragen".replace(
                "%studentfirstname",
                student.firstname,
              )}
        </label>
      ) : (
        <span></span>
      )}
      <div style="display: flex; flex-direction: column;">
        {renderedEnrollmentRequests}
      </div>

      <br />

      {enrollments.length > 0 ? (
        <label>
          {student.tag === "all"
            ? "inschrijvingen"
            : "%studentfirstname's inschrijvingen".replace(
                "%studentfirstname",
                student.firstname,
              )}
        </label>
      ) : (
        <span></span>
      )}
      <div style="display: flex; flex-direction: column;">
        {enrollmentsRecent.map((e) =>
          renderEnrollmentCard(
            {
              showStudentName: student.tag === "all",
              extraHtml: renderEnrollmentLinks(e),
            },
            e,
          ),
        )}
      </div>

      {renderShowEnrollmentsOverSection(
        enrollmentsOlder.length,
        showEnrollmentsOlderS,
      )}

      {dyn(showEnrollmentsOlderS, (showEnrollmentsOlder) => {
        return showEnrollmentsOlder ? (
          <div style="display: flex; flex-direction: column;">
            {enrollmentsOlder.map((e) =>
              renderEnrollmentCard(
                {
                  showStudentName: student.tag === "all",
                  extraHtml: renderEnrollmentLinks(e),
                },
                e,
              ),
            )}
          </div>
        ) : (
          <span></span>
        );
      })}

      <br />
      <br />

      {waitinglistprivateitems.length + waitinglistgroupitems.length > 0 ? (
        <label>{"wachtlijst"}</label>
      ) : (
        <span></span>
      )}
      <div style="display: flex; flex-direction: column;">
        {waitinglistprivateitems.map((wli) =>
          renderWaitinglistItem(
            { showStudentName: student.tag === "all" },
            wli,
          ),
        )}
      </div>
      <div style="display: flex; flex-direction: column;">
        {waitinglistgroupitems.map((wli) =>
          renderWaitinglistItem(
            { showStudentName: student.tag === "all" },
            wli,
          ),
        )}
      </div>
    </div>
  );
}

function renderEnrollmentLinks(
  e: enrollmentStudentStart,
): HTMLElement | HTMLElement[] | undefined {
  const today = LocalDate.now();
  return (
    <div className={style.card_student_card_links} style="margin-top: 16px;">
      <a
        href={routes.student_start_lessons_notesRoute.link({
          enrollmentid: e.id,
          lessongroupid: e.lessongroup.id,
        })}
      >
        {"Lesdata & notities"}
      </a>
      {e.status === "ActiveEnrollment" &&
      e.lessongroup.lastlesson &&
      (e.lessongroup.lastlesson.isEqual(today) ||
        e.lessongroup.lastlesson.isAfter(today)) ? (
        <a
          href={routes.student_start_absencesRoute.link({
            enrollmentid: e.id,
            lessongroupid: e.lessongroup.id,
          })}
        >
          {"Afwezig"}
        </a>
      ) : (
        []
      )}
      {e.evaluation_count_for_students > 0 ? (
        <a
          href={routes.student_start_evaluationsRoute.link({
            enrollmentid: e.id,
            lessongroupid: e.lessongroup.id,
          })}
        >
          {"Evaluaties"}
        </a>
      ) : (
        []
      )}
      {e.has_mutuality_certificate === true ? (
        <a
          href={routes.downloadMutualityCertificateSpec.route.link({
            enrollmentid: e.id,
          })}
          download="certificate.pdf"
        >
          {"Attest mutualiteit"}
        </a>
      ) : (
        []
      )}
    </div>
  );
}

function renderEnrollmentrequestStatus(
  _id: types.enrollmentrequestid,
  s: types.enrollmentrequest_status,
): { text: string; link?: string } | null {
  const parsed = parseEnrollmentRequestStatus(s);
  switch (parsed) {
    case "InvitationER":
      return { text: "Voorstel herinschrijving" };
    case "Reserved":
      return { text: "Voorstel herinschrijving" };
    case "WaitingForPaymentER":
      return {
        text: "Inschrijving wachtend op betaling",
      };
    case "WaitingForApprovalER":
      return { text: "Inschrijving wachtend op goedkeuring" };
    case "WaitingForPaymentAtProviderER":
      return null;
    case "InvitationRejectedER":
      return null;
    case "DisprovedER":
      return null;
    case "ConvertedToEnrollment":
      return null;
    case "WaitingForEmailConfirmationER":
    case "WaitingForEmailConfirmationTimedOutER":
    case "WaitingForPaymentTimedOutER":
    case "WaitingForPaymentAtProviderTimedOutER":
      return null;
    default:
      return checkAllCasesHandled(parsed);
  }
}

export function renderEnrollmentCard(
  opts: {
    showStudentName: boolean;
    extraHtml?: HTMLElement | HTMLElement[];
    style?: string;
  },
  e: {
    student: { firstname: string; lastname: string };
    status: types.enrollment_status;
    lessongroup: {
      time: LocalTime;
      weekday: types.weekday;
      firstlesson: LocalDate | null;
      lastlesson: LocalDate | null;
    };
    instrument: { description: string };
    teacher: { firstname: string; lastname: string };
    room_description: string | null;
  },
): HTMLElement {
  const contents = (
    <div>
      <h3>{"Inschrijving"}</h3>
      <h2>
        {"%instrument bij %teacher"
          .replace("%instrument", e.instrument.description)
          .replace("%teacher", renderFirstNameLastName(e.teacher))}
      </h2>

      <article className={style.equal_columns_non_responsive}>
        {opts.showStudentName ? (
          <div className={style.text_with_icon}>
            <i className={FA.icon_user_solid}></i>
            <div style={"text-decoration: none;"}>
              {capitalize(e.student.firstname.toLowerCase())}
            </div>
          </div>
        ) : (
          []
        )}
        <div
          className={style.text_with_icon}
          style={opts.showStudentName ? "flex: 0 1 auto;" : ""}
        >
          <i className={FA.icon_clock_regular}></i>
          <div style={"text-decoration: none;"}>
            {renderTimePlusWeekday(
              e.lessongroup.time,
              parseWeekday(e.lessongroup.weekday),
            )}
          </div>
        </div>
      </article>

      <div className={style.equal_columns_non_responsive}>
        <div className={style.text_with_icon} style="margin-bottom: 8px;">
          <i className={FA.icon_calendar_alt_regular}></i>
          <div style={"text-decoration: none;"}>
            {(e.lessongroup.firstlesson
              ? ddMMMyyyy(e.lessongroup.firstlesson)
              : "") +
              (e.lessongroup.lastlesson &&
              !isEqual(e.lessongroup.lastlesson, e.lessongroup.firstlesson)
                ? " - " + ddMMMyyyy(e.lessongroup.lastlesson)
                : "")}
          </div>
        </div>
        {e.room_description ? (
          <div className={style.text_with_icon} style="flex: 0 1 auto;">
            <i className={FA.icon_map_marker_alt_solid}></i>
            <div style={"text-decoration: none;"}>{e.room_description}</div>
          </div>
        ) : (
          <span></span>
        )}
      </div>

      {e.status === "Stopped" ? (
        <div className={style.text_with_icon} style="padding-top: 16px;">
          <i className={FA.icon_exclamation_circle_solid}></i>
          <div style={"text-decoration: none;"}>{"Stopgezet"}</div>
        </div>
      ) : e.status === "TrialLesson" ? (
        <div className={style.text_with_icon} style="padding-top: 16px;">
          <i className={FA.icon_exclamation_circle_solid}></i>
          <div style={"text-decoration: none;"}>{"Proefles"}</div>
        </div>
      ) : (
        <span></span>
      )}

      {opts.extraHtml || []}
    </div>
  );
  return (
    <div className={style.card_student_start} style={opts.style || ""}>
      {contents}
    </div>
  );
}

export function renderWaitinglistItem(
  opts: {
    showStudentName: boolean;
  },
  wli: waitinglistprivateitem | waitinglistgroupitem,
): HTMLElement {
  const contents = (
    <div>
      <h3>{"Wachtlijst"}</h3>
      <h2>
        {("firstlesson" in wli
          ? renderFormulaTypeShort(wli.type)
          : "Privelés") + " "}
        {wli.instrument.description}{" "}
        {"firstlesson" in wli && wli.type.tag === "Camp"
          ? ""
          : wli.teacher.firstname && wli.teacher.lastname
          ? " " +
            "bij %teacher".replace(
              "%teacher",
              renderFirstNameLastName({
                firstname: wli.teacher.firstname,
                lastname: wli.teacher.lastname,
              }),
            )
          : ""}
      </h2>

      {opts.showStudentName ? (
        <article className={style.text_with_icon}>
          <i className={FA.icon_user_solid}></i>
          <div style={"text-decoration: none;"}>
            {capitalize(wli.student.firstname.toLowerCase())}
          </div>
        </article>
      ) : (
        []
      )}

      {"firstlesson" in wli ? (
        <div className={style.text_with_icon} style="margin-bottom: 8px;">
          <i className={FA.icon_clipboard_solid}></i>
          <div style={"text-decoration: none;"}>
            {"Eerste les: %date".replace(
              "%date",
              wli.firstlesson ? ddMMMMyyyy(wli.firstlesson) : "",
            )}
          </div>
        </div>
      ) : (
        <div className={style.equal_columns_non_responsive}>
          <div>
            {(wli.formulas || []).map((f) => {
              return (
                <div
                  className={style.text_with_icon}
                  style="margin-bottom: 8px;"
                >
                  <i className={FA.icon_clipboard_solid}></i>
                  <div style={"text-decoration: none;"}>{f.description}</div>
                </div>
              );
            })}
          </div>
          <div>
            {(wli.ranges || []).map((r) => {
              return (
                <div
                  className={style.text_with_icon}
                  style="margin-bottom: 8px;"
                >
                  <i className={FA.icon_clock_regular}></i>
                  <div style={"text-decoration: none;"}>
                    {renderTimeRangePlusWeekday(
                      r.starttime,
                      r.endtime,
                      parseWeekday(r.weekday),
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
  return <div className={style.card_student_start}>{contents}</div>;
}

export function studentSectionRenderStudentSelection(
  students: {
    id: types.studentid;
    firstname: string;
    lastname: string;
  }[],
  studentS: Source<
    | { tag: "all" }
    | {
        tag: "student";
        id: types.studentid;
        firstname: string;
        lastname: string;
      }
  >,
): HTMLElement {
  if (students.length < 2) {
    return <span></span>;
  }
  const curr = studentS.get();
  const stringS = new Source(curr.tag === "all" ? "all" : curr.id.toString());

  scheduleForCleanup(
    stringS.observe((id) => {
      if (id === "all") {
        studentS.set({ tag: "all" });
      } else {
        const found = students.find((s) => s.id.toString() === id);
        if (found) {
          studentS.set({ tag: "student", ...found });
        }
      }
    }),
  );

  return (
    <div
      className={style.full_width_center_800}
      style="background-color: var(--grey-200);"
    >
      {selectbox({
        source: stringS,
        options: students
          .orderBy((stu) => renderFirstNameLastName(stu))
          .map((stu) => ({
            value: stu.id.toString(),
            label: renderFirstNameLastName(stu),
          }))
          .concat([{ value: "all", label: "Iedereen" }]),
        style: "background-color: white; font-weight: bold;",
        label: "Student",
      })}
    </div>
  );
}

export function studentSectionInitStudentidSelection(
  students: {
    id: types.studentid;
    firstname: string;
    lastname: string;
  }[],
): Source<
  | { tag: "all" }
  | { tag: "student"; id: types.studentid; firstname: string; lastname: string }
> {
  const lsCutoff = Instant.now().minusSeconds(1 * 60 * 60 * 24); // After 24 hours we reset
  const fromLs = getFromLocalStorage(studentSectionStudentidSelectionLSSpec);
  const initialStudentSelection =
    !isNil(fromLs) && fromLs.stamp.isAfter(lsCutoff)
      ? fromLs.tag === "all"
        ? "all"
        : students.find((s) => s.id === fromLs.studentid) || students[0]
      : "all";

  const initialVal =
    initialStudentSelection === "all"
      ? { tag: "all" as const }
      : { tag: "student" as const, ...initialStudentSelection };

  const s = new Source(initialVal);
  scheduleForCleanup(
    s.observe((stu) =>
      stu.tag === "all"
        ? setLocalStorage(studentSectionStudentidSelectionLSSpec, {
            tag: "all",
            stamp: Instant.now(),
          })
        : setLocalStorage(studentSectionStudentidSelectionLSSpec, {
            tag: "studentid",
            studentid: stu.id,
            stamp: Instant.now(),
          }),
    ),
  );
  return s;
}

function renderEnrollmentRequest(
  student: { tag: "all" } | { tag: "student"; firstname: string },
  e: privateEnrollmentrequest | groupEnrollmentrequest,
) {
  const renderedStatus = renderEnrollmentrequestStatus(e.id, e.status);
  if (renderedStatus === null) {
    return null;
  } else {
    const card = (
      <div
        className={style.card_student_start}
        style="border-color: var(--grey-400);"
      >
        <h3>{renderedStatus.text}</h3>
        <h2>
          {e.teacher.firstname && e.teacher.lastname
            ? "%instrument bij %teacher"
                .replace("%instrument", e.instrument.description)
                .replace(
                  "%teacher",
                  renderFirstNameLastName({
                    firstname: e.teacher.firstname,
                    lastname: e.teacher.lastname,
                  }),
                )
            : e.instrument.description}
        </h2>

        <div className={style.equal_columns_non_responsive}>
          {student.tag === "all" ? (
            <div className={style.text_with_icon}>
              <i className={FA.icon_user_solid}></i>
              <div style="text-decoration: none;">
                {capitalize(e.student.firstname.toLowerCase())}
              </div>
            </div>
          ) : (
            []
          )}
          <div
            className={style.text_with_icon}
            style={student.tag === "all" ? "flex: 0 1 auto;" : ""}
          >
            <i className={FA.icon_clock_regular}></i>
            <div>{renderTimePlusWeekday(e.time, parseWeekday(e.weekday))}</div>
          </div>
        </div>
      </div>
    );
    if (renderedStatus.link) {
      return <a href={renderedStatus.link}>{card}</a>;
    } else {
      return card;
    }
  }
}

export function renderShowEnrollmentsOverSection(
  filteredEnrollmentsOverLength: number,
  showEnrollmentsOverS: Source<boolean>,
) {
  return dyn(showEnrollmentsOverS, (showEnrollmentsOver) => {
    const seperator =
      filteredEnrollmentsOverLength > 0 ? (
        <div className={style.period_line}>
          <span>{"Oudere inschrijvingen"}</span>
          <hr />
        </div>
      ) : (
        <span></span>
      );

    const button =
      !showEnrollmentsOver && filteredEnrollmentsOverLength > 0 ? (
        <button
          style="width: 100%;"
          className={style.button_white}
          onclick={() => showEnrollmentsOverS.set(true)}
        >
          {"Toon alle inschrijvingen"}
        </button>
      ) : (
        <span></span>
      );

    return [seperator, button];
  });
}
